import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  Provider,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { AUTH_CONFIG, provideAuth } from '@auth';
import {
  COMMON_CONFIG,
  CookieProService,
  OLRVersion,
  requestActionInterceptor,
  requestSourceInterceptor,
  whiteLabelClientInterceptor,
} from '@common';
import {
  authConfigFactory,
  commonConfigFactory,
  Config,
  CONFIG,
  ENVIRONMENT,
  provideConfig,
} from '@config';
import { DOMAIN_ID, provideContentDefaults } from '@content';
import {
  gaHttpErrorInterceptor,
  posthogHttpErrorInterceptor,
} from '@domgen/d2c-shared/util-analytics';
import { GA_CONFIG, gaConfigFactory, provideTracking } from '@ga';
import { provideI18nDefaults } from '@i18n';
import { provideSEO, SEO_CONFIG, seoConfigFactory } from '@seo';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaModule,
  RecaptchaV3Module,
} from 'ng-recaptcha-2';
import { environment } from 'src/environments/environment';
import { appRoutes } from '../routes/app-tv.routes';

export function provideApp(): Provider | EnvironmentProviders[] {
  return [
    provideAuth(),
    provideSEO(),
    provideAppInitializer(() => {
      if (!isDevMode()) {
        inject(CookieProService).initializeCookiePro();
      }
    }),
    provideZonelessChangeDetection(),
    provideContentDefaults({ olrVersion: OLRVersion.SidedoorTV }),
    provideI18nDefaults(),
    provideRouter(
      appRoutes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(
      withInterceptors([
        requestSourceInterceptor,
        requestActionInterceptor,
        whiteLabelClientInterceptor,
        gaHttpErrorInterceptor,
        posthogHttpErrorInterceptor,
      ]),
    ),
    importProvidersFrom(RecaptchaModule, RecaptchaV3Module),
    provideConfig(),
    provideTracking(),
    {
      provide: AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: COMMON_CONFIG,
      useFactory: commonConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: DOMAIN_ID,
      useFactory: (config: Config) => config.domainId,
      deps: [CONFIG],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: GA_CONFIG,
      useFactory: gaConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (config: Config) => config.recaptchaSiteKey,
      deps: [CONFIG],
    },
    {
      provide: SEO_CONFIG,
      useFactory: seoConfigFactory,
      deps: [CONFIG],
    },
  ];
}
